import { CircularProgress, Stack, List, ListItem, Tooltip, Icon } from '@mui/material';
import { Commit, ChangeCircle, DeleteSweep, Warning, Beenhere } from '@mui/icons-material';
import { Link } from 'react-router';
import {
  TooltipHeader,
  DateCell,
  CellList,
  PrecisionCell,
  CheckboxFilter,
  PercentageCell,
} from '../util/DataTableCells';
import { LIST_DELIMITER, MISSING_VALUE } from '../util/data-table';
import { buildStagingLink, editableCellProps } from './staging';
import PointTypeSelector from './selectors/PointTypeSelector';
import EquipmentTypeSelector from './selectors/EquipmentTypeSelector';
import UnitSelector from './selectors/UnitSelector';

import NameSelector from './selectors/NameSelector';
import { tooltips } from './tooltips';
import { unique } from '../../utils/collections';
import { capitalize } from '../../utils/strings';

export function enrichColHeaderWithTooltip(col) {
  if (!Object.prototype.hasOwnProperty.call(tooltips, col.id)) return col;
  const TooltipWrapper = (details) => (
    <TooltipHeader
      tooltip={tooltips[col.id]}
      content={
        Object.prototype.hasOwnProperty.call(col, 'Header') ? col.Header(details) : col.header
      }
    />
  );
  TooltipWrapper.displayName = 'TooltipWrapper';
  return { ...col, Header: TooltipWrapper };
}

export function enrichColAccessorFnWithMissing(col) {
  return {
    ...col,
    accessorFn: (details) => {
      const result = col.accessorFn
        ? col.accessorFn(details)
        : (details[col?.accessorKey] ?? details[col.id]);
      if (result?.length === 0) return MISSING_VALUE;
      return result ?? MISSING_VALUE;
    },
  };
}

export const colBase = {
  enableEditing: false,
  Edit: () => null,
};

const superColumn = (subColumns) => ({
  ...colBase,
  subColumns,
  id: 'super',
  accessorFn: (entity) => subColumns.map((sc) => sc.accessorFn(entity)),
  header: 'Flags',
  Filter: ({ table }) => (
    <Stack justifyContent="center" direction="row">
      {subColumns.map(enrichColHeaderWithTooltip).map((sc) => (
        <Stack key={sc.id} direction="column">
          {sc.Header()}
          {sc.Filter({ column: table.getColumn(sc.id) })}
        </Stack>
      ))}
    </Stack>
  ),
  Cell: ({ table, row, renderedCellValue }) => (
    <Stack justifyContent="center" direction="row">
      {subColumns.map((sc, idx) => (
        <div key={sc.id}>
          {sc.Cell({ table, row, renderedCellValue: renderedCellValue?.[idx] })}
        </div>
      ))}
    </Stack>
  ),
});
export function deviceIdCol(getter) {
  return {
    ...colBase,
    id: 'deviceId',
    accessorFn: getter,
    filterVariant: 'multi-select',
    header: 'Device ID',
  };
}

export function deviceNameCol(getter) {
  return {
    ...colBase,
    id: 'deviceName',
    accessorFn: getter,
    accessorKey: 'name',
    header: 'Device Name',
    filterVariant: 'autocomplete',
  };
}

export function deploymentNameCol(getter) {
  return {
    ...colBase,
    id: 'deploymentName',
    header: 'Deployment Name',
    filterVariant: 'select',
    accessorFn: getter,
  };
}

export function deviceProtocolCol(getter) {
  return {
    ...colBase,
    accessorFn: getter,
    id: 'deviceProtocol',
    header: 'Collection Protocol',
    filterVariant: 'select',
  };
}
export function deviceModelCol(getter) {
  return {
    ...colBase,
    accessorFn: getter,
    id: 'deviceModel',
    header: 'Device Model',
    filterVariant: 'autocomplete',
  };
}
export function deviceVendorCol(getter) {
  return {
    ...colBase,
    accessorFn: getter,
    id: 'deviceVendor',
    header: 'Device Vendor',
    filterVariant: 'autocomplete',
  };
}
export function deviceDescriptionCol(getter) {
  return {
    ...colBase,
    accessorFn: getter,
    id: 'deviceDescription',
    header: 'Device Description',
  };
}

export const firstDiscoveredCol = {
  ...colBase,
  id: 'firstDiscoveredTime',
  accessorKey: 'created',
  header: 'First Discovered Time',
  filterVariant: 'datetime-range',
  Cell: DateCell,
};
export const lastDiscoveredCol = {
  ...colBase,
  id: 'lastDiscoveredTime',
  accessorKey: 'lastDiscovery',
  header: 'Last Discovered Time',
  filterVariant: 'datetime-range',
  Cell: DateCell,
};
export const lastPublishedCol = {
  ...colBase,
  id: 'lastPublishedTime',
  accessorKey: 'lastPublished',
  header: 'Last Published Time',
  filterVariant: 'datetime-range',
  Cell: DateCell,
};
const BooleanCol = (id, getter, icon, optionalTooltip) => ({
  ...colBase,
  id,
  accessorFn: getter,
  header: `Is ${capitalize(id)}`,
  Header: () => icon,
  Filter: CheckboxFilter,
  Cell: ({ row, renderedCellValue, key }) => {
    if (renderedCellValue === undefined)
      return <CircularProgress size="18px" aria-label="Loading…" />;
    if (renderedCellValue)
      return optionalTooltip ? (
        <Tooltip title={optionalTooltip(row)} key={key}>
          <span aria-label={undefined}>{icon}</span>
        </Tooltip>
      ) : (
        icon
      );
    return <Icon key={key} />;
  },
});
const isPublishedCol = BooleanCol(
  'published',
  (pe) => Boolean(pe.publishedPointId || pe.publishedEquipmentId),
  <Beenhere color="success" />
);

const isInvalidId = 'validation';
export const pointEquipNamesId = 'pointEquipNames';
function buildValidationTooltip(entity) {
  const pointValidationIssues = entity.points?.some((p) => p.validation?.length > 0) ? (
    <>
      Has Point Errors (
      <Link
        to={buildStagingLink(window.location.pathname.split('/')[2], 'points', {
          [isInvalidId]: true,
          [pointEquipNamesId]: entity.name,
        })}
      >
        view
      </Link>
      )
    </>
  ) : null;
  const entityValidationIssues =
    entity.validation?.length !== 0 ? (
      <>
        Contains validation errors:
        <List sx={{ listStyleType: 'disc' }}>
          {entity.validation?.map((v) => (
            <ListItem key={v.field + v.error_class} sx={{ display: 'list-item', padding: 0 }}>
              {v.field} - {v.message}
            </ListItem>
          ))}
        </List>
      </>
    ) : null;
  return (
    <>
      {entityValidationIssues} {pointValidationIssues}
    </>
  );
}
function buildDiffTooltip(diffFields) {
  return <>{diffFields.join(', ')} do not match last published version</>;
}

const isInvalidCol = BooleanCol(
  isInvalidId,
  (entity) => {
    if (
      entity.validation === undefined ||
      (entity.point ? entity.points?.some((p) => p.validation === undefined) : false)
    )
      return undefined;
    return (
      entity.validation?.length > 0 ||
      (entity.points ? entity.points?.some((p) => p.validation?.length > 0) : false)
    );
  },
  <Warning color="error" />,
  (row) => buildValidationTooltip(row.original)
);

const hasDiffCol = BooleanCol(
  'diff',
  ({ diff }) => diff?.length > 0,
  <ChangeCircle color="error" />,
  (row) => buildDiffTooltip(row.original.diff)
);

const isAssignedCol = BooleanCol(
  'pointAssigned',
  (p) => p?.equip?.length > 0,
  <Commit color="success" />
);

const taggerPointUnassignedId = 'taggerPointUnassigned';
const isTaggerRemovedCol = BooleanCol(
  taggerPointUnassignedId,
  // If we do not know, do not erroneously mark as tagger unassigned
  (p) => !(p.data?.['auto_tagger.point_assignment'] ?? true),
  <DeleteSweep color="warning" />
);

export const defaultFilters = Object.fromEntries([isAssignedCol.id].map((k) => [k, 'true']));

export const pointObjectIdentifierId = 'pointObjectIdentifier';
export const pointTopicId = 'pointTopic';
export const pointNameId = 'pointName';
export const pointTypeId = 'pointType';
export const pointTypeConfidenceId = 'pointTypeConfidence';
export const pointDescriptionId = 'pointDescription';
export const pointPresentValueId = 'presentValue';
export const pointUnitsId = 'units';
export const pointRawUnitId = 'protocolUnit';
export const pointSuperColumn = superColumn([
  isPublishedCol,
  isInvalidCol,
  hasDiffCol,
  isAssignedCol,
  isTaggerRemovedCol,
]);
export const pointColumns = (
  canWrite,
  pointTypes,
  units,
  equipNames,
  equipmentTypes,
  pSuperColumn,
  stagingPointUpdates
) => {
  const pointTypeLabels = Object.values(pointTypes).map(({ id, name }) => ({
    label: name,
    value: id,
  }));
  const unitLabels = Object.values(units).map(({ id, nameLong, nameAbbr }) => ({
    label: `${nameLong} (${nameAbbr})`,
    value: id,
  }));

  const rowPointTypeSelector = ({ row }) => (
    <PointTypeSelector
      entities={[row.original]}
      onChange={(pt) =>
        stagingPointUpdates(
          [row.original].map((p) => ({
            topic: p.topic,
            point_type: { id: pt?.id ?? null, confidence: 100 },
          }))
        )
      }
    />
  );
  const rowUnitSelector = ({ row }) => (
    <UnitSelector
      entities={[row.original]}
      onChange={(u) =>
        stagingPointUpdates(
          [row.original].map((p) => ({
            topic: p.topic,
            raw_unit: { id: u?.id ?? null, confidence: 100 },
          }))
        )
      }
    />
  );
  return [
    pSuperColumn,
    {
      ...colBase,
      id: pointNameId,
      accessorFn: (p) => p.properties?.objectName,
      header: 'Point Name',
      filterVariant: 'autocomplete',
    },
    {
      ...colBase,
      id: pointTopicId,
      accessorFn: (p) => p.topic,
      header: 'Point Topic',
      filterVariant: 'autocomplete',
    },
    {
      ...colBase,
      id: pointTypeId,
      // Keep the space in front to sort first
      accessorFn: (p) => p?.pointType?.name ?? MISSING_VALUE,
      header: 'Point Type',
      // filter matching does not work with custom function
      enableFilterMatchHighlighting: false,
      enableColumnFilterModes: false,
      sortingFn: (rowA, rowB, columnId) =>
        rowA.getValue(columnId).localeCompare(rowB.getValue(columnId)),
      filterFn: (row, id, filterValue) => {
        const value = row.getValue(id);
        return filterValue.split(' ')?.every((segment) => value?.includes(segment));
      },
      enableEditing: stagingPointUpdates !== null,
      Edit: rowPointTypeSelector,
      editVariant: 'select',
      editSelectOptions: pointTypeLabels,
      Cell: ({ renderedCellValue }) => renderedCellValue,
      muiTableBodyCellProps: canWrite ? editableCellProps : null,
    },
    {
      ...colBase,
      id: pointTypeConfidenceId,
      accessorFn: (p) => p.confidences?.point_type_id,
      header: 'Point Type Confidence',
      filterVariant: 'range',
      Cell: PercentageCell,
    },
    {
      ...colBase,
      id: pointDescriptionId,
      accessorFn: (p) => p.properties?.description,
      header: 'Point Description',
    },
    {
      ...colBase,
      id: pointPresentValueId,
      accessorFn: (p) => p.properties?.presentValue,
      header: 'Present Value',
      Cell: ({ cell }) => PrecisionCell(cell, 2),
    },
    {
      ...colBase,
      id: pointUnitsId,
      // Keep the space in front to sort first
      accessorFn: (p) => p?.rawUnitId,
      header: 'Units',
      filterVariant: 'select',
      filterSelectOptions: unitLabels,
      enableEditing: stagingPointUpdates !== null,
      Edit: rowUnitSelector,
      editVariant: 'select',
      editSelectOptions: unitLabels,
      Cell: ({ row }) => row.original.unit?.name_abbr,
      muiTableBodyCellProps: canWrite ? editableCellProps : null,
    },
    {
      ...colBase,
      id: pointRawUnitId,
      accessorFn: (p) => p.properties?.units,
      header: 'Protocol Units',
      filterVariant: 'autocomplete',
    },
    {
      ...colBase,
      id: pointEquipNamesId,
      accessorFn: (p) => p.equip?.map((e) => e.name)?.join(LIST_DELIMITER),
      header: 'Equip Names',
      filterFn: 'arrIncludes',
      filterVariant: 'autocomplete',
      filterSelectOptions: equipNames,
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'pointEquipTypes',
      accessorFn: (p) =>
        unique(p.equip?.map((e) => e.equipmentType?.nameAbbr))?.join(LIST_DELIMITER),
      header: 'Equip Types',
      filterFn: 'arrIncludes',
      filterVariant: 'select',
      filterSelectOptions: Object.values(equipmentTypes).map(({ tagName }) => tagName),
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    deviceIdCol((p) => p.device.deviceId),
    {
      ...colBase,
      id: pointObjectIdentifierId,
      accessorFn: (p) => p.properties?.objectIdentifier?.join(', ') || undefined,
      header: 'Object Identifier',
      filterVariant: 'autocomplete',
    },
    firstDiscoveredCol,
    lastDiscoveredCol,
    {
      ...colBase,
      id: 'alarmValue',
      accessorFn: (p) => p.properties?.alarmValue,
      header: 'Alarm Value',
      filterVariant: 'select',
    },
    {
      ...colBase,
      id: 'activeInactive',
      accessorFn: (p) =>
        p.properties?.activeText && p.properties?.inactiveText
          ? [p.properties?.activeText, p.properties?.inactiveText].join(' / ')
          : undefined,
      header: 'Active/Inactive',
      filterVariant: 'select',
    },
    lastPublishedCol,
  ]
    .concat(pSuperColumn.subColumns)
    .map(enrichColAccessorFnWithMissing)
    .map(enrichColHeaderWithTooltip);
};

export const equipmentNameId = 'equipmentName';
export const equipmentIdId = 'equipmentId';
export const equipmentTypeId = 'equipmentType';
export const equipmentConfidenceId = 'equipmentTypeConfidence';
export const equipmentDevicesNamesId = 'devicesName';
export const equipmentDevicesModelsId = 'devicesModel';
export const equipmentSuperColumn = superColumn([isPublishedCol, isInvalidCol, hasDiffCol]);
export const equipmentPointsSuperColumn = superColumn([isAssignedCol, isTaggerRemovedCol]);
export const equipmentColumns = (canWrite, equipmentTypes, usedNames, stagingEquipmentUpdates) => {
  const equipmentTypeLabels = Object.values(equipmentTypes).map(({ id, nameAbbr }) => ({
    label: nameAbbr,
    value: id,
  }));

  return [
    equipmentSuperColumn,
    {
      ...colBase,
      id: equipmentNameId,
      accessorKey: 'name',
      header: 'Equipment Name',
      filterVariant: 'autocomplete',
      enableEditing: true,
      Edit: ({ table, row }) => (
        <NameSelector
          name={row.original.name}
          suggestedName={row.original.data.suggested_equip_name}
          usedNames={usedNames}
          onChange={(newName) =>
            stagingEquipmentUpdates([{ equip_id: row.original.equipId, equip_name: newName }])
          }
          onBlur={() => table.setEditingCell(null)}
        />
      ),
      muiTableBodyCellProps: canWrite ? editableCellProps : null,
    },
    {
      ...colBase,
      id: equipmentIdId,
      accessorKey: 'equipId',
      header: 'EquipId',
      filterVariant: 'autocomplete',
    },
    {
      ...colBase,
      id: equipmentTypeId,
      // Keep the space in front to sort first
      accessorFn: (e) => e?.equipmentType?.nameAbbr ?? '',
      header: 'Equipment Type',
      enableEditing: true,
      Edit: ({ row }) => (
        <EquipmentTypeSelector
          entities={[row.original]}
          onChange={(et) =>
            stagingEquipmentUpdates([
              { equip_id: row.original.equipId, equipment_type: { id: et.id, confidence: 100 } },
            ])
          }
        />
      ),
      editVariant: 'select',
      editSelectOptions: equipmentTypeLabels,
      muiTableBodyCellProps: canWrite ? editableCellProps : null,
    },
    {
      ...colBase,
      id: equipmentConfidenceId,
      accessorFn: (e) => e.confidences?.equipment_type_id,
      header: 'Equipment Type Confidence',
      filterVariant: 'range',
      Cell: PercentageCell,
    },
    {
      ...colBase,
      id: 'devicesId',
      accessorFn: (e) => e.devices?.map((d) => d.deviceId)?.join(LIST_DELIMITER),
      header: 'Devices ID',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: equipmentDevicesNamesId,
      accessorFn: (e) => e.devices?.map((d) => d.name)?.join(LIST_DELIMITER),
      header: 'Devices Name',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesProtocol',
      accessorFn: (e) => unique(e.devices?.map((d) => d.protocol))?.join(LIST_DELIMITER),
      header: 'Devices Protocol',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: equipmentDevicesModelsId,
      accessorFn: (e) =>
        unique(e.devices?.map((d) => d.properties.modelName))?.join(LIST_DELIMITER),
      header: 'Devices Model',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesVendor',
      accessorFn: (e) =>
        unique(e.devices?.map((d) => d.properties.vendorName))?.join(LIST_DELIMITER),
      header: 'Devices Vendor',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesSoftware',
      header: 'Devices Software',
      accessorFn: (e) =>
        e.devices?.map((d) => d?.properties?.applicationSoftwareVersion)?.join(LIST_DELIMITER),
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesPrograms',
      header: 'Devices Programs',
      accessorFn: (e) =>
        // eslint-disable-next-line no-underscore-dangle
        unique(e.devices?.flatMap((d) => Object.values(d.properties?._programs ?? {}))).join(
          LIST_DELIMITER
        ),
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesDescription',
      accessorFn: (e) =>
        unique(e.devices?.map((d) => d.properties.description).filter(Boolean)).join(
          LIST_DELIMITER
        ),
      header: 'Devices Description',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'deploymentsName',
      accessorFn: (e) => unique(e.devices?.map((d) => d.deployment?.name))?.join(LIST_DELIMITER),
      header: 'Deployments Names',
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesLocation',
      header: 'Devices Location',
      accessorFn: (e) =>
        e.devices
          ?.map((d) => d?.properties?.location)
          .filter(Boolean)
          .join(LIST_DELIMITER),
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    {
      ...colBase,
      id: 'devicesProfile',
      header: 'Devices Profile',
      accessorFn: (e) =>
        e.devices
          ?.map((d) => d?.properties?.profileName)
          .filter(Boolean)
          .join(LIST_DELIMITER),
      Cell: ({ cell }) => CellList(cell.getValue()),
    },
    lastPublishedCol,
  ]
    .concat(equipmentSuperColumn.subColumns)
    .map(enrichColAccessorFnWithMissing)
    .map(enrichColHeaderWithTooltip);
};

export const equipmentFeedsId = 'equipmentFeedsConfidence';
export function equipmentFeedsCol(targetEquipment) {
  return {
    ...colBase,
    id: equipmentFeedsId,
    accessorFn: (p) =>
      p.data['ref_tagger.fed'] === targetEquipment.equipId
        ? p.confidences?.['ref_tagger.fed']
        : MISSING_VALUE,
    header: 'Feeds Confidence',
    filterVariant: 'range',
    Cell: PercentageCell,
  };
}
export const locationNameId = 'locationName';
export const locationTypeId = 'locationType';
export const locationColumns = (usedNames, locationModify) =>
  [
    {
      ...colBase,
      id: locationNameId,
      accessorKey: 'name',
      header: 'Location Name',
      filterVariant: 'autocomplete',
      enableEditing: true,
      Edit: ({ row }) => (
        <NameSelector
          name={row.original.name}
          usedNames={usedNames}
          onChange={(newName) => locationModify(row.original.name, newName)}
        />
      ),
    },
    {
      ...colBase,
      id: locationTypeId,
      accessorKey: 'type',
      header: 'Location Type',
    },
    {
      ...colBase,
      id: 'locationLocationCount',
      header: 'Contains Locations Count',
      accessorFn: (l) => l.containsLocations?.length,
      filterVariant: 'range',
    },
    {
      ...colBase,
      id: 'locationEquipmentCount',
      header: 'Contains Equipment Count',
      accessorFn: (l) => l.containsEquipment?.length,
      filterVariant: 'range',
    },
    {
      ...colBase,
      id: 'locationCreatedTime',
      accessorKey: 'created',
      header: 'Location Created Time',
      filterVariant: 'datetime-range',
      Cell: DateCell,
    },
  ]

    .map(enrichColAccessorFnWithMissing)
    .map(enrichColHeaderWithTooltip);
